import { cn } from '@/lib/cn';
import { useMergeRefs } from '@floating-ui/react';
import * as RadixDialog from '@radix-ui/react-dialog';
import TooltipContainerContext from 'contexts/TooltipContainerContext';
import { ReactNode, forwardRef, useRef } from 'react';

export const Dialog: React.FC<React.ComponentPropsWithoutRef<typeof RadixDialog.Root>> = ({ children, ...props }) => (
    <RadixDialog.Root {...props}>{children}</RadixDialog.Root>
);

export const DialogTrigger: React.FC = ({ children }) => <RadixDialog.Trigger asChild>{children}</RadixDialog.Trigger>;

export const DialogTitle: React.FC<React.ComponentPropsWithoutRef<typeof RadixDialog.Title>> = ({
    children,
    ...props
}) => <RadixDialog.DialogTitle {...props}>{children}</RadixDialog.DialogTitle>;

interface DialogContentProps {
    className?: string;
    children?: ReactNode;
    /**
     * The id of the dialog. Required if this dialog is shown above another dialog.
     * Helps avoid duplicate ids in the DOM.
     */
    id?: string;
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
    ({ className, children, id, ...props }, ref) => {
        const dialogRef = useRef<HTMLDivElement>(null);
        const refs = useMergeRefs([ref, dialogRef]);

        return (
            <RadixDialog.Portal>
                <RadixDialog.Overlay className='fixed inset-0 bg-modalBackground backdrop-filter backdrop-blur-md' />
                <RadixDialog.Content className={cn(className)} id={id ?? 'dialogContent'} ref={refs} {...props}>
                    <TooltipContainerContext.Provider value={dialogRef.current}>
                        {children}
                    </TooltipContainerContext.Provider>
                </RadixDialog.Content>
            </RadixDialog.Portal>
        );
    }
);
